import { api } from "@/lib/api";

// One client for both parties: the supplier uses their session, the architect only the invite token.
export const client = (token) => {
  const base = token ? `/condoor/a/${token}` : "/condoor/projects";
  return {
    project: (pid) => api.get(token ? base : `${base}/${pid}`).then((r) => r.data),
    result: (pid) => api.get(token ? `${base}/result` : `${base}/${pid}/result`).then((r) => r.data),
    fileUrl: (pid, role) => `${api.defaults.baseURL}${token ? `${base}/files/${role}` : `${base}/${pid}/files/${role}`}`,
    decide: (pid, finding, choice) => api.post(`${base}/${pid}/decide`, { finding, choice }),
    undo: (pid, finding) => api.post(`${base}/${pid}/undo`, { finding, choice: "keep" }),
    send: (pid) => api.post(`${base}/${pid}/send`).then((r) => r.data),
    retry: (pid) => api.post(`${base}/${pid}/retry`),
    review: (finding, action, note) => api.post(`${base}/review`, { finding, action, note }),
    addLine: (door, text) => api.post(`${base}/lines`, { door, text }).then((r) => r.data),
    approve: () => api.post(`${base}/approve`).then((r) => r.data),
    chat: (pid, text) => api.post(token ? `${base}/chat` : `${base}/${pid}/chat`, { text }).then((r) => r.data),
  };
};

export const ORIGIN = {
  generated: { label: "Generated by Condoor", short: "Generated", cls: "b-gen", tag: "tag-generated", color: "var(--gen)" },
  architect: { label: "Contract documents · architect", short: "Architect", cls: "b-arch", tag: "tag-architect", color: "var(--arch)" },
  code: { label: "Public code · internet", short: "Internet", cls: "b-code", tag: "tag-code", color: "var(--code)" },
  catalog: { label: "Catalog · supplier only", short: "Acme only", cls: "b-cat", tag: "tag-catalog", color: "var(--cat)" },
};

export const FLAG = {
  conflict: { label: "Conflict", cls: "b-conflict" },
  substitution: { label: "Substitution", cls: "b-substitution" },
  confirm: { label: "Confirm", cls: "b-confirm" },
  assumption: { label: "Assumption", cls: "b-assumption" },
};

// Where a source lives: which file opens when you click its tag.
export const docOf = (src) => {
  if (!src) return null;
  if (src.origin === "code") return `code:${src.doc}`;
  if (src.origin === "catalog") return `cut:${src.sheet}`;
  return src.doc; // spec | schedule | interfaces
};

export const docLabel = (key, data) => {
  if (key === "submittal") return "08 71 00-001 Submittal";
  if (key === "spec") return data?.spec?.doc?.title ? `${data.spec.doc.id} Specification` : "Specification";
  if (key === "schedule") return `${data?.schedule?.doc?.id || "A-601"} Door & Frame Schedule`;
  if (key === "interfaces") return `${data?.interfaces?.doc?.id || "SEC-701"} Security Interfaces`;
  if (key === "catalog") return "Catalog";
  if (key.startsWith("code:")) return (data?.codes || []).find((c) => c.id === key.slice(5))?.title?.split(" · ")[0] || "Code";
  if (key.startsWith("cut:")) return data?.cut_sheets?.[key.slice(4)]?.file || "Cut sheet";
  return key;
};

export const originOfDoc = (key) =>
  key === "submittal" ? "generated" : key === "catalog" || key.startsWith("cut:") ? "catalog" : key.startsWith("code:") ? "code" : "architect";

// Supplier decision → line status shown everywhere.
export const lineStatus = (line, finding, decisions) => {
  if (!finding) return { label: "Met", cls: "b-met" };
  const d = decisions?.[finding.id] || {};
  if (d.architect === "edited") return { label: "Edited", cls: "b-edited" };
  if (d.architect === "accepted") return { label: finding.kind === "assumption" ? "Confirmed" : "Approved", cls: "b-resolved" };
  if (d.supplier === "alt") return { label: "Other source used", cls: "b-other" };
  if (d.supplier === "keep") return { label: finding.kind === "assumption" ? "Sent as assumption" : "Resolved", cls: "b-resolved" };
  return FLAG[finding.kind] || { label: finding.kind, cls: "b-conflict" };
};

export const fmtClock = (s) => {
  if (s == null || Number.isNaN(s)) return "--:--";
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

export const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export const norm = (s) => String(s ?? "").replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/\s+/g, " ").trim().toLowerCase();

// Printed labels for each opening field, used to find the exact cell a field-level source points at.
export const FIELD_PRINTED = {
  width_in: ["width"], height_in: ["height"], leaf_material: ["leaf material", "leaf"], leaf_thickness_in: ["leaf thickness", "leaf thk"],
  frame_material: ["frame material", "frame"], frame_throat_in: ["frame throat", "throat"], rating_min: ["assembly rating", "rating"],
  stc: ["stc target", "stc"], hand: ["hand"], finish: ["lock trim finish", "lock finish"], key_group: ["key group"],
  access_control: ["access control", "access"], lock_voltage: ["lock voltage"], power_loss: ["power loss ingress", "power loss"],
  egress: ["egress"], closer: ["closer"], threshold: ["threshold"], seals: ["seals"], need_on_site: ["need on site"], set: ["hw set", "hardware set"],
  remarks: ["remarks"], voltage: ["lock", "voltage"], supplied_by: ["supplied by"], wired_by: ["wired by"],
};
